/**
 * React Query hooks for the Integrations screen.
 *
 * Mutations invalidate both the affected detail query and the
 * upstream list + overview so per-provider KPI cards stay in sync
 * after a status change.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EcomListParams,
  fetchEcommerceIntegration,
  fetchEcommerceIntegrations,
  fetchIntegrationsOverview,
  IntegrationStatus,
  updateEcommerceIntegration
} from '@/api/integrations';

export function useIntegrationsOverview() {
  return useQuery({
    queryKey: ['integrationsOverview'],
    queryFn: fetchIntegrationsOverview,
    refetchInterval: 30_000
  });
}

export function useEcommerceIntegrations(params: EcomListParams) {
  return useQuery({
    queryKey: ['ecommerceIntegrations', params],
    queryFn: () => fetchEcommerceIntegrations(params),
    placeholderData: prev => prev
  });
}

export function useEcommerceIntegration(id: string | null) {
  return useQuery({
    queryKey: ['ecommerceIntegration', id],
    queryFn: () => fetchEcommerceIntegration(id!),
    enabled: Boolean(id)
  });
}

export function useUpdateEcommerceIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; status?: IntegrationStatus }) =>
      updateEcommerceIntegration(input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['ecommerceIntegration', vars.id] });
      qc.invalidateQueries({ queryKey: ['ecommerceIntegrations'] });
      qc.invalidateQueries({ queryKey: ['integrationsOverview'] });
    }
  });
}
